import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect } from "react";
import { useApiMutation, useApiQuery, useTypedQueryParams } from "../../hooks";
import type { AppConfig } from "../../types";
import type { SuccessResponse } from "../../types/api";
import { TestSelect } from "../test-select";

export const NewTestTab = () => {
  const { params } = useTypedQueryParams<{ url?: string; task?: string }>();

  const [url, setUrl] = React.useState<string>("");
  const [appConfigId, setAppConfigId] = React.useState<string | null>(null);
  const [task, setTask] = React.useState<string>("");
  const [testDep, setTestDep] = React.useState<string | null>(null);

  useEffect(() => {
    if (params?.url) {
      setUrl(params.url);
    }
  }, [params?.url]);

  useEffect(() => {
    if (params?.task) {
      setTask(params.task);
    }
  }, [params?.task]);

  const { enqueueSnackbar } = useSnackbar();

  const { data, refetch } = useApiQuery<AppConfig[]>("/vibe-qa/app-configs", {
    refetchInterval: 10_000,
  });

  const { mutate, isPending } = useApiMutation<
    {
      url?: string;
      task: string;
      dep_id?: string;
      app_config_id?: string;
    },
    SuccessResponse
  >("POST", "/vibe-qa/sessions", {
    onSuccess: () => {
      setUrl("");
      setTask("");
      setTestDep(null);
      setAppConfigId(null);

      refetch();

      enqueueSnackbar("Session started!", { variant: "success" });
    },
  });

  return (
    <Box sx={{ maxWidth: 600, mx: "auto" }}>
      <Card>
        <CardContent>
          <Stack direction="column" alignItems="stretch" spacing={2}>
            <Typography variant="h5" gutterBottom>
              What should we test next?
            </Typography>

            <TestSelect
              value={testDep}
              onChange={(newValue) => setTestDep(newValue)}
              label="(Optional) Start from existing test"
              size="small"
              path="/vibe-qa/tests"
              appConfigId={appConfigId ?? undefined}
            />

            <Autocomplete
              options={data ?? []}
              getOptionLabel={(option) => {
                if (typeof option === "string") {
                  return option;
                }
                return option.base_url || "about:blank";
              }}
              value={data?.find((config) => config.base_url === url) ?? null}
              inputValue={url}
              onChange={(_, newValue) => {
                if (typeof newValue === "string") {
                  setUrl(newValue);
                } else if (newValue) {
                  setUrl(newValue.base_url || "");
                  setAppConfigId(newValue.id);
                } else {
                  setUrl("");
                }
              }}
              onInputChange={(_, newInputValue) => {
                setUrl(newInputValue);

                const maybeApp = data?.find((config) => config.base_url === newInputValue);

                if (maybeApp) {
                  setAppConfigId(maybeApp.id);
                } else {
                  setAppConfigId(null);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="URL to test"
                  placeholder="E.g., 'https://myapp.com'"
                  size="small"
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                  >
                    <Stack direction="column" spacing={0} alignItems="flex-start">
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        sx={{
                          fontSize: "0.875rem",
                          fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                        }}
                      >
                        {option.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: "0.75rem",
                          fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                        }}
                      >
                        {option.base_url ?? "about:blank"}
                      </Typography>
                    </Stack>
                    <Chip label={option.cfg_type ?? "legacy"} size="small" />
                  </Stack>
                </Box>
              )}
              freeSolo
              isOptionEqualToValue={(option, value) => option.id === value.id}
              fullWidth
            />

            <TextField
              label="What do you want to test?"
              placeholder="E.g., 'Test the login functionality and ensure that users can log in with valid credentials.'"
              variant="outlined"
              fullWidth
              multiline
              minRows={4}
              maxRows={20}
              value={task}
              onChange={(e) => setTask(e.target.value)}
            />

            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                loading={isPending}
                disabled={(url.length === 0 && !appConfigId) || task.length === 0 || isPending}
                onClick={() => {
                  if ((url.length === 0 && !appConfigId) || task.length === 0) {
                    return;
                  }

                  mutate({
                    app_config_id: appConfigId ?? undefined,
                    url: url.length > 0 ? url : undefined,
                    task,
                    dep_id: testDep ?? undefined,
                  });
                }}
              >
                Start Session
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};
